import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'motion/react';
import { useAppUpdate } from '@/hooks/useAppUpdate';
import { useUpdatedToast } from '@/hooks/useUpdatedToast';
import { reloadToLatestVersion } from '@/lib/serviceWorkerUpdate';
import './AppUpdate.css';

/** Пружина появления — SPRING из mrbooker (client/src/motion/config.ts). */
const RISE_SPRING = { type: 'spring', stiffness: 480, damping: 34, mass: 0.8 } as const;

export interface UpdateBarProps {
  onUpdate: () => void;
  /** «Обновить» уже нажата: на кнопке лоадер, пока браузер качает сборку и перезагружается. */
  updating?: boolean;
}

/**
 * Плашка «Доступна новая версия» с лаймовой «Обновить» — как на ПК в mrbooker (там — «Вышла новая версия»). Сама плашка
 * неподвижна, нажимается только «Обновить»; закрыть плашку нельзя, она ждёт, пока человек обновится. С reduced motion
 * появляется сразу на месте.
 */
export function UpdateBar({ onUpdate, updating = false }: UpdateBarProps) {
  const reduced = useReducedMotion() ?? false;
  // В body и с .nd-portal: общие правила старого дизайна для button (верхний регистр, интервал) пропускают .nd-portal,
  // и в Storybook плашка оказывается вне .nd-root, как в приложении
  return createPortal(
    <div className="app-update-host nd-portal">
      <motion.div
        className="app-update-bar"
        initial={reduced ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={RISE_SPRING}
      >
        <span>Доступна новая версия</span>
        <button
          type="button"
          className="app-update-bar__action"
          onClick={onUpdate}
          disabled={updating}
          aria-busy={updating || undefined}
        >
          {/* Ширина та же: надпись прозрачная, по центру лоадер (как Button loading в newdesign/ui) */}
          <span className="app-update-bar__label">Обновить</span>
          {updating ? <span aria-hidden="true" className="nd-spinner app-update-bar__spinner" /> : null}
        </button>
      </motion.div>
    </div>,
    document.body
  );
}

/** Плашка, когда вышла новая версия, и тост после обновления; критичную перезагружает сам useAppUpdate. */
export function AppUpdate() {
  const available = useAppUpdate();
  const [updating, setUpdating] = useState(false);
  useUpdatedToast();
  if (!available) return null;
  // Сброса нет: reloadToLatestVersion всегда заканчивается перезагрузкой вкладки
  const update = () => {
    setUpdating(true);
    void reloadToLatestVersion();
  };
  return <UpdateBar onUpdate={update} updating={updating} />;
}
